import {
  ChangeDetectionStrategy,
  Component,
  inject,
  output,
  signal,
} from '@angular/core';
import { NgbActiveOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { UiTranslationService } from '@core/services/ui-translation.service';
import { MindmapTranslations } from '@/app/views/mindmap/mindmap-translations';
import {
  MINDMAP_THEME_PALETTES,
  type MindmapVisualTheme,
} from '@/app/views/mindmap/mindmap-theme-palettes';

export type { MindmapVisualTheme } from '@/app/views/mindmap/mindmap-theme-palettes';

export type MindmapLayout = 'mindmap' | 'org-chart' | 'list';

type LayoutOption = {
  id: MindmapLayout;
  labelKey: 'appearanceLayoutMindmap' | 'appearanceLayoutOrgChart';
};

type ThemeOption = {
  id: MindmapVisualTheme;
  labelKey:
    | 'appearanceThemeColorfull'
    | 'appearanceThemeVintage'
    | 'appearanceThemeBlast'
    | 'appearanceThemePrism'
    | 'appearanceThemeAquarelle'
    | 'appearanceThemeSunny'
    | 'appearanceThemeOcean'
    | 'appearanceThemePastel'
    | 'appearanceThemeBubble';
  swatches: readonly string[];
};

const getThemePreview = (theme: MindmapVisualTheme): readonly string[] => {
  const palette = MINDMAP_THEME_PALETTES[theme];

  if (palette.length <= 6) {
    return palette;
  }

  return Array.from(
    { length: 6 },
    (_, index) => palette[Math.round((index * (palette.length - 1)) / 5)],
  );
};

@Component({
  selector: 'app-mindmap-appearance-offcanvas',
  standalone: true,
  templateUrl: './mindmap-appearance-offcanvas.html',
  styleUrl: './mindmap-appearance-offcanvas.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MindmapAppearanceOffcanvasComponent {
  private readonly activeOffcanvas = inject(NgbActiveOffcanvas);
  private readonly uiTranslation = inject(UiTranslationService);

  protected readonly selectedLayout = signal<MindmapLayout>('mindmap');
  protected readonly selectedTheme = signal<MindmapVisualTheme>('colorful');

  readonly themeChange = output<MindmapVisualTheme>();
  readonly layoutChange = output<MindmapLayout>();

  protected readonly themeOptions: readonly ThemeOption[] = [
    {
      id: 'colorful',
      labelKey: 'appearanceThemeColorfull',
      swatches: getThemePreview('colorful'),
    },
    {
      id: 'vintage',
      labelKey: 'appearanceThemeVintage',
      swatches: getThemePreview('vintage'),
    },
    {
      id: 'blast',
      labelKey: 'appearanceThemeBlast',
      swatches: getThemePreview('blast'),
    },
    {
      id: 'prism',
      labelKey: 'appearanceThemePrism',
      swatches: getThemePreview('prism'),
    },
    {
      id: 'aquarelle',
      labelKey: 'appearanceThemeAquarelle',
      swatches: getThemePreview('aquarelle'),
    },
    {
      id: 'sunny',
      labelKey: 'appearanceThemeSunny',
      swatches: getThemePreview('sunny'),
    },
    {
      id: 'ocean',
      labelKey: 'appearanceThemeOcean',
      swatches: getThemePreview('ocean'),
    },
    {
      id: 'pastel',
      labelKey: 'appearanceThemePastel',
      swatches: getThemePreview('pastel'),
    },
    {
      id: 'bubble',
      labelKey: 'appearanceThemeBubble',
      swatches: getThemePreview('bubble'),
    },
  ];

  protected readonly layoutOptions: readonly LayoutOption[] = [
    { id: 'mindmap', labelKey: 'appearanceLayoutMindmap' },
    { id: 'org-chart', labelKey: 'appearanceLayoutOrgChart' },
  ];

  protected readonly i18n =
    this.uiTranslation.namespace<MindmapTranslations>('mindmap');

  selectLayout(layout: MindmapLayout): void {
    this.selectedLayout.set(layout);
    this.layoutChange.emit(layout);
  }

  setInitialLayout(layout: MindmapLayout): void {
    this.selectedLayout.set(layout);
  }

  setInitialTheme(theme: MindmapVisualTheme): void {
    this.selectedTheme.set(theme);
  }

  selectTheme(theme: MindmapVisualTheme): void {
    this.selectedTheme.set(theme);
    this.themeChange.emit(theme);
  }

  close(): void {
    this.activeOffcanvas.dismiss();
  }
}
